import {
  Box,
  Button,
  Flex,
  Icon,
  Input,
  InputElement,
  Spinner,
  Wrap,
} from '@chakra-ui/react';
import React from 'react';
import { MdArrowDropDown, MdClose } from 'react-icons/md';
import MCustomIconButton from '../../MCustomIconButton';
import { MainInputProps } from './MainInput';
import { useCustomSelectInput } from './useCustomSelectInput';

export const MainSearchInputV2 = React.forwardRef<
  HTMLInputElement,
  MainInputProps
>((props, ref) => {
  const {
    disabled,
    externalLoading,
    inputProps,
    isOpen,
    isReadOnly,
    loading,
    onOpen,
    onQueryChange,
    onToggleOpen,
    placeholder,
    renderContentInInput,
    showContentInInput,
  } = props;

  const {
    bgColor,
    borderColor,
    handleOnClear,
    handleClickInput,
    handleFocusInput,
    handleToggleOpen,
    inputValue,
    rightElement,
    rightMargin,
    showClearBtn,
    styles,
  } = useCustomSelectInput(props, ref);

  // `isolation` stands in for v2's `InputGroup`; see the note in MainInput.
  if (!showContentInInput) {
    return (
      <Box position="relative" isolation="isolate">
        <Input
          type="text"
          ref={ref}
          placeholder={placeholder}
          onChange={(e: any) => {
            !isOpen && onOpen(); // open if required
            onQueryChange(e.target.value);
          }}
          value={inputValue}
          onClick={handleClickInput}
          disabled={disabled}
          readOnly={isReadOnly}
          variant={isReadOnly ? 'readonly' : 'outline'}
          {...(isOpen
            ? {
                bg: bgColor,
                borderColor: 'tBlue.lightShade',
                color: 'tIndigo.base',
              }
            : {})}
          tabIndex={isOpen ? -1 : 0}
          pr={`${rightMargin * 4}px`}
          cursor="pointer"
          title={inputValue}
          {...inputProps}
          // Last so it cannot be re-enabled by inputProps. The search box is
          // never itself required, but it inherits FormControl context from an
          // enclosing `MFormField isRequired`.
          required={false}
        />
        {!isReadOnly && (
          <InputElement
            placement="end"
            width={`${rightMargin * 4}px`}
            px="0"
            top="0"
          >
            {rightElement?.content}
            {showClearBtn && (
              <MCustomIconButton
                icon={MdClose}
                aria-label="Clear"
                bg="none"
                color="tPurple.dark"
                _hover={{ bg: 'none' }}
                ml="2"
                btnSize={4}
                padding={0}
                containerSize={4}
                onClick={handleOnClear}
              />
            )}
            {loading || externalLoading ? (
              <Spinner size="xs" />
            ) : (
              <Button
                role="button"
                variant="icon"
                padding="0"
                minWidth="initial"
                _hover={{ background: 'none' }}
                _focus={{ background: 'none', boxShadow: 'none' }}
                tabIndex={-1}
              >
                <Icon fontSize="20" asChild>
                  <MdArrowDropDown onMouseDown={handleToggleOpen} />
                </Icon>
              </Button>
            )}
          </InputElement>
        )}
      </Box>
    );
  }
  return (
    <Box
      css={styles}
      // Only color the chrome when the popover is open; resting state takes
      // its border from the V2 Input theme.
      {...(isOpen ? { bg: bgColor, borderColor } : {})}
      display="flex"
      flexWrap="wrap"
      height="auto"
      position="relative"
      isolation="isolate"
      alignItems="center"
      px="1"
    >
      <Wrap gap={0.5}>
        {showContentInInput &&
          renderContentInInput &&
          renderContentInInput({ onToggleOpen })}
        <Flex alignItems="center" flex="1" display="grid">
          <Input
            type="text"
            ref={ref}
            placeholder={placeholder}
            onChange={() => {}}
            value={inputValue}
            onFocus={handleFocusInput}
            disabled={disabled}
            tabIndex={isOpen ? -1 : 0}
            border="none"
            _focus={{ border: 'none' }}
            width="auto"
            bg="none"
            _placeholder={{
              color: 'tGray.darkGrayPurple',
            }}
            title={inputValue}
            {...inputProps}
          />
        </Flex>
      </Wrap>
      <Box
        onMouseDown={handleToggleOpen}
        position="absolute"
        right="0"
        bottom="1"
      >
        {loading || externalLoading ? (
          <Spinner size="xs" />
        ) : (
          <Button
            role="button"
            variant="icon"
            padding="0"
            minWidth="initial"
            _hover={{ background: 'none' }}
            _focus={{ background: 'none', boxShadow: 'none' }}
            tabIndex={-1}
          >
            <Icon fontSize="20" zIndex="1" asChild>
              <MdArrowDropDown />
            </Icon>
          </Button>
        )}
      </Box>
    </Box>
  );
});
